import type { ClientMessage } from "@shared/protocol";
import { useAppStore } from "../store/appStore";
import { Ico } from "./icons";
import { Avatar, StatusDot } from "./atoms";

interface Props {
  send: (msg: ClientMessage) => boolean;
}

export function TeamDrawer({ send }: Props) {
  const { teammateDrawer, closeTeammate, team, commits, panels, upsertPanel } = useAppStore();
  if (!teammateDrawer) return null;
  const member = team.find((t) => t.supabaseId === teammateDrawer);
  if (!member) return null;

  const userCommits = commits.filter((c) => c.user === member.displayName).slice(0, 8);
  const sessions = panels.filter((p) => p.ownerSupabaseId === member.supabaseId);

  const watch = (agentId: string) => {
    send({ type: "session:watch", payload: { agentId, targetUser: member.linuxUser } });
    const existing = panels.find((p) => p.agentId === agentId);
    if (existing) {
      upsertPanel({ ...existing, readonly: true });
    }
    closeTeammate();
  };

  return (
    <>
      <div className="drawer-scrim" onClick={closeTeammate} />
      <aside className="drawer">
        <header className="drawer-head">
          <Avatar member={member} size={36} />
          <div className="title">
            <div className="name">{member.displayName}</div>
            <div className="meta">
              {member.online ? "online" : "offline"} · {member.agents} Session{member.agents !== 1 ? "s" : ""}
              {member.branch ? ` · ${member.branch}-fokus` : ""}
            </div>
          </div>
          <button className="icon-btn" onClick={closeTeammate} style={{ width: 28, height: 28 }}>
            <Ico name="x" size={16} />
          </button>
        </header>

        <div className="drawer-body">
          <div className="drawer-section">
            <h4>
              <span className="eyebrow">Laufende Sessions</span>
              <span className="subtle" style={{ fontSize: 11 }}>readonly</span>
            </h4>
            {sessions.length === 0 && (
              <div className="subtle" style={{ fontSize: 12 }}>Keine Sessions sichtbar.</div>
            )}
            {sessions.map((s) => (
              <div key={s.agentId} className="session-card" onClick={() => watch(s.agentId)}>
                <div className="top">
                  <StatusDot status={s.status} />
                  <span className="task">{s.task}</span>
                  <button className="icon-btn" title="Session anschauen"><Ico name="eye" size={14} /></button>
                </div>
                <div className="branch"><Ico name="branch" size={10} />{s.workspaceName}</div>
              </div>
            ))}
          </div>

          <div className="drawer-section">
            <h4>
              <span className="eyebrow">Letzte Commits</span>
              <span className="subtle" style={{ fontSize: 11 }}>{userCommits.length} sichtbar</span>
            </h4>
            {userCommits.map((c) => (
              <div key={c.hash + c.timestamp} className="commit-row">
                <span className="hash">{c.hash}</span>
                <div>
                  <div className="msg">{c.message}</div>
                  <div className="branch">{c.project} · {c.branch}</div>
                </div>
                <span className="time">{relativeTime(c.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </>
  );
}

function relativeTime(ts: number): string {
  const diff = (Date.now() - ts) / 1000;
  if (diff < 60) return "gerade eben";
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} h`;
  return `vor ${Math.floor(diff / 86400)} d`;
}
